import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/lib/reportError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Root-level error boundary. Wraps <App /> in main.tsx.
 *
 * Goals:
 * - Stop a single render error from white-screening the whole app.
 * - Log the error through `reportError` so it ends up wherever the error
 *   tracker of the day is pointed.
 * - Give the user a friendly "something went wrong, please refresh" screen
 *   with a refresh action. No stack traces in prod.
 * - In dev, show the error message so debugging is faster.
 *
 * This is deliberately a class component — React still requires class
 * components for error boundaries (as of React 18).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError('errorBoundary.render', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  handleRefresh = () => {
    // Hard reload; clears the broken render state and picks up any new SW.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = (() => {
        try {
          return Boolean((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV);
        } catch {
          return false;
        }
      })();

      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: '#1a1a1a',
            background: '#fafafa',
          }}
        >
          <div style={{ maxWidth: '420px' }}>
            <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', fontWeight: 600 }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '1.5rem', color: '#555' }}>
              TripBoom hit an unexpected error. We've logged it and our team will take a look.
              Please refresh the page to try again.
            </p>
            <button
              type="button"
              onClick={this.handleRefresh}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: 500,
                color: '#fff',
                background: '#0f766e',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              aria-label="Refresh the page"
            >
              Refresh
            </button>
            {isDev && this.state.error && (
              <pre
                style={{
                  marginTop: '2rem',
                  padding: '1rem',
                  background: '#1e1e1e',
                  color: '#f1f1f1',
                  borderRadius: '6px',
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: '240px',
                }}
              >
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
